import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Athlete, Team, User, Category } from './types';
import { SHIRT_PRICE, TOURNAMENT_NAME } from './constants';
import { getRegistrationFee, formatCurrency } from './utils';
import AthleteForm from './components/AthleteForm';
import TeamManager from './components/TeamManager';
import Stats from './components/Stats';
import Login from './components/Login';
import * as XLSX from 'xlsx';

type ViewState = 'public' | 'auth' | 'dashboard';
// IMPORTANTE: Ajuste esta URL para onde seus arquivos PHP estão sendo servidos.
// Se você colocar a pasta 'api' dentro do htdocs do XAMPP, a URL será algo como http://localhost/api
// Se estiver usando VSCode PHP Server, pode ser outra porta.
const API_URL = './api'; 

const App: React.FC = () => {
  const [view, setView] = useState<ViewState>('auth');
  const [targetRole, setTargetRole] = useState<'ADMIN' | 'MANAGER'>('MANAGER');
  const [toast, setToast] = useState<{ message: string, type: 'success' | 'info' } | null>(null);
  const [lastRegisteredAthlete, setLastRegisteredAthlete] = useState<Athlete | null>(null);
  
  const showToast = (message: string, type: 'success' | 'info' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'teams' | 'athletes' | 'users'>('teams');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [systemUsers, setSystemUsers] = useState<any[]>([]);

  // Check for existing session
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setCurrentUser(JSON.parse(savedUser));
      setView('dashboard');
    }
  }, []);

  // Fetch Teams
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const token = localStorage.getItem('token');
        const endpoint = currentUser ? `${API_URL}/teams.php` : `${API_URL}/teams.php?public=true`;
        const headers: HeadersInit = currentUser ? { 'Authorization': `Bearer ${token}` } : {};
        const res = await fetch(endpoint, { headers });
        if (res.ok) setTeams(await res.json());
      } catch (error) { console.error(error); }
    };
    fetchTeams();
  }, [currentUser]);

  // Fetch Athletes (Only when logged in)
  useEffect(() => {
    if (!currentUser) return;
    const fetchAthletes = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/athletes.php`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setAthletes(await res.json());
      } catch (error) { console.error(error); }
    };
    fetchAthletes();
  }, [currentUser]);

  // Fetch Users (Only for ADMIN)
  useEffect(() => {
    if (currentUser?.role !== 'ADMIN') return;
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/users.php`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) setSystemUsers(await res.json());
      } catch (error) { console.error(error); }
    };
    fetchUsers();
  }, [currentUser]);

  const handleDeleteAthlete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este atleta?")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/athletes.php?id=${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setAthletes(prev => prev.filter(a => a.id !== id));
        showToast("Atleta removido.");
      }
    } catch (error) { console.error(error); }
  };

  const handleUpdateAthlete = async (id: string, data: any) => {
    try {
      const token = localStorage.getItem('token');
      // Recalcular valores para segurança
      const regFee = getRegistrationFee(data.registrationLevel);
      const shirtV = data.hasShirt ? SHIRT_PRICE : 0;
      const payload = { ...data, registrationFee: regFee, shirtValue: shirtV, totalValue: regFee + shirtV };

      const res = await fetch(`${API_URL}/athletes.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        const refresh = await fetch(`${API_URL}/athletes.php`, { headers: { 'Authorization': `Bearer ${token}` } });
        setAthletes(await refresh.json());
        setIsModalOpen(false);
        setEditingItem(null);
        showToast("Dados atualizados!");
      }
    } catch (error) { console.error(error); }
  };

  const handleManageUser = async (data: any) => {
    const token = localStorage.getItem('token');
    const method = editingItem ? 'PUT' : 'POST';
    const url = editingItem ? `${API_URL}/users.php?id=${editingItem.id}` : `${API_URL}/users.php`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        const refresh = await fetch(`${API_URL}/users.php`, { headers: { 'Authorization': `Bearer ${token}` } });
        setSystemUsers(await refresh.json());
        setIsModalOpen(false);
        setEditingItem(null);
        showToast(editingItem ? "Usuário atualizado" : "Usuário criado");
      } else {
        const err = await res.json();
        showToast(err.error || "Erro na operação", "info");
      }
    } catch (error) { console.error(error); }
  };

  const handleDeleteUser = async (id: string) => {
    if (!confirm("Remover este usuário?")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users.php?id=${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setSystemUsers(prev => prev.filter(u => u.id !== id));
        showToast("Usuário removido.");
      } else {
        const err = await res.json();
        showToast(err.error, "info");
      }
    } catch (error) { console.error(error); }
  };

  const openEditAthlete = (athlete: Athlete) => {
    setEditingItem(athlete);
    setIsModalOpen(true);
  };

  const openAddAthlete = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const openManageUser = (u?: any) => {
    setEditingItem(u || null);
    setIsModalOpen(true);
  };



  const handleLogin = async (username: string, password: string): Promise<User | null> => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        setCurrentUser(data.user);
        setView('public'); // Enviar para o Passo 2 após o login
        showToast(`Bem-vindo, ${data.user.name}!`, 'info');
        return data.user;
      }
      return null;
    } catch (error) {
      console.error("Login failed", error);
      return null;
    }
  };

  const handleRegister = async (name: string, username: string, password: string) => {
    try {
      const res = await fetch(`${API_URL}/auth.php?action=register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, username, password })
      });

      if (res.ok) {
        const loginUser = await handleLogin(username, password);
        if (loginUser) {
           showToast("Conta de gestor criada!");
        }
      } else {
        const msg = await res.text();
        showToast(`Erro ao criar conta: ${msg}`, 'info');
      }
    } catch (error) {
      console.error("Registration failed", error);
      showToast("Erro de conexão.", 'info');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setCurrentUser(null);
    setView('auth');
    setLastRegisteredAthlete(null);
    setAthletes([]);
  };

  const handleAddAthlete = useCallback(async (newAthleteData: any) => {
    const regFee = getRegistrationFee(newAthleteData.registrationLevel);
    const actualShirtValue = newAthleteData.hasShirt ? SHIRT_PRICE : 0;
    
    const athletePayload = {
      ...newAthleteData,
      registrationFee: regFee,
      shirtValue: actualShirtValue,
      totalValue: regFee + actualShirtValue,
    };
    
    try {
      const token = localStorage.getItem('token');
      // Se não houver token, usamos o parâmetro public=true
      const endpoint = token ? `${API_URL}/athletes.php` : `${API_URL}/athletes.php?public=true`;
      
      const headers: HeadersInit = { 
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(athletePayload)
      });

      const data = await res.json();

      if (res.ok) {
        if (token) {
          const refresh = await fetch(`${API_URL}/athletes.php`, { headers: { 'Authorization': `Bearer ${token}` } });
          setAthletes(await refresh.json());
        } else {
          setLastRegisteredAthlete({ id: data.id, fullName: data.fullName } as any);
        }
        showToast("Inscrição realizada!");
        setIsModalOpen(false);
      } else {
        // Mostra o erro específico retornado pelo PHP (ex: CPF duplicado)
        showToast(data.message || data.error || "Erro ao salvar inscrição.", 'info');
      }
    } catch (error) {
      console.error("Error saving athlete", error);
      showToast("Erro de conexão.", 'info');
    }
  }, [currentUser]);

  const handleAddTeam = useCallback(async (newTeamData: any) => {
    if (!currentUser) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/teams.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(newTeamData)
      });

      const data = await res.json();
      if (res.ok) {
        setTeams(prev => [...prev, data]);
        showToast("Equipe criada!");
      } else {
        showToast(data.error || "Erro ao criar equipe.", 'info');
      }
    } catch (error) {
      console.error(error);
      showToast("Erro de conexão.", 'info');
    }
  }, [currentUser]);

  const handleUpdateTeam = async (id: string, data: any) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/teams.php?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      if (res.ok) {
        const refresh = await fetch(`${API_URL}/teams.php`, { headers: { 'Authorization': `Bearer ${token}` } });
        setTeams(await refresh.json());
        setIsModalOpen(false);
        setEditingItem(null);
        showToast("Equipe atualizada!");
      } else {
        const err = await res.json();
        showToast(err.error || "Erro ao atualizar equipe.", 'info');
      }
    } catch (error) { console.error(error); }
  };

  const handleDeleteTeam = async (id: string) => {
    if (!confirm("Remover esta equipe permanentemente?")) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/teams.php?id=${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setTeams(prev => prev.filter(t => t.id !== id));
        showToast("Equipe removida.");
      } else {
        const err = await res.json();
        showToast(err.error || "Erro ao remover equipe.", 'info');
      }
    } catch (error) { console.error(error); }
  };

  const handleExportToExcel = () => {
    const excelData = athletes.map(a => ({
      'Nome': a.fullName, 'CPF': a.cpf, 'Categoria': a.category, 'Total': formatCurrency(a.totalValue)
    }));
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inscritos");
    XLSX.writeFile(wb, `Inscritos_Copa_Volei.xlsx`);
  };

  const visibleAthletes = useMemo(() => athletes, [athletes]);
  const visibleTeams = useMemo(() => teams, [teams]);

  // Views
  if (view === 'public') {
    return (
      <div className="min-h-screen bg-slate-50 pb-20">
        <header className="bg-blue-700 text-white py-14 px-4 text-center">
          <h1 className="text-3xl md:text-5xl font-black mb-3">{TOURNAMENT_NAME}</h1>
          <p className="opacity-80">Inscrição de Atletas</p>
        </header>
        <main className="max-w-4xl mx-auto px-4 -mt-10">
          {lastRegisteredAthlete ? (
            <div className="bg-white p-12 rounded-[2rem] shadow-xl text-center">
              <h2 className="text-3xl font-black mb-4">Inscrição Realizada!</h2>
              <p className="mb-8">Protocolo: {lastRegisteredAthlete.id.toUpperCase()}</p>
              <button onClick={() => setLastRegisteredAthlete(null)} className="bg-blue-600 text-white px-8 py-4 rounded-xl font-bold">Nova Inscrição</button>
            </div>
          ) : (
            <>
              <AthleteForm onAdd={handleAddAthlete} teams={teams} />
              <div className="text-center mt-8">
                <button 
                  onClick={() => setView('dashboard')} 
                  className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] hover:text-slate-600 transition-colors"
                >
                  Área Administrativa
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    );
  }

  if (view === 'auth') {
    return <Login targetRole={targetRole} onLogin={handleLogin} onRegister={handleRegister} onSwitchRole={setTargetRole} onBack={() => {}} />;
  }

  if (!currentUser) return null;

  return (
    <div className="min-h-screen bg-slate-100 pb-20">
      <nav className="bg-slate-900 text-white p-6 flex justify-between items-center">
        <h1 className="font-black uppercase tracking-tighter">Painel de Gestão</h1>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setView('public')}
            className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            Inscrição
          </button>
          <button onClick={handleLogout} className="bg-red-500/10 hover:bg-red-500 text-red-500 hover:text-white px-4 py-2 rounded-xl font-bold text-xs transition-all">Sair</button>
        </div>
      </nav>
      <main className="max-w-6xl mx-auto p-6">
        <div className="flex gap-4 mb-8 overflow-x-auto pb-2 scrollbar-none">
          {currentUser.role === 'ADMIN' && (
            <button onClick={() => setActiveTab('overview')} className={`px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === 'overview' ? 'bg-blue-600 text-white shadow-xl shadow-blue-200' : 'bg-white text-slate-400 hover:text-slate-600 shadow-sm'}`}>Painel Geral</button>
          )}
          <button onClick={() => setActiveTab('teams')} className={`px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === 'teams' ? 'bg-blue-600 text-white shadow-xl shadow-blue-200' : 'bg-white text-slate-400 hover:text-slate-600 shadow-sm'}`}>Equipes</button>
          <button onClick={() => setActiveTab('athletes')} className={`px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === 'athletes' ? 'bg-blue-600 text-white shadow-xl shadow-blue-200' : 'bg-white text-slate-400 hover:text-slate-600 shadow-sm'}`}>Atletas</button>
          {currentUser.role === 'ADMIN' && (
            <button onClick={() => setActiveTab('users')} className={`px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-widest whitespace-nowrap transition-all ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-xl shadow-blue-200' : 'bg-white text-slate-400 hover:text-slate-600 shadow-sm'}`}>Usuários</button>
          )}
        </div>

        {activeTab === 'overview' && currentUser.role === 'ADMIN' ? (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <Stats athletes={athletes} teams={teams} />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(Category).map(cat => {
                const catTeams = teams.filter(t => t.category === cat);
                const catAthletes = athletes.filter(a => a.category === cat);
                return (
                  <div key={cat} className="bg-white p-6 rounded-[2rem] border border-slate-50 shadow-sm hover:border-blue-100 transition-all group">
                    <h3 className="text-[10px] font-black uppercase text-blue-600 tracking-widest mb-4 inline-block px-2 py-0.5 bg-blue-50 rounded-md">{cat}</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[24px] font-black text-slate-900 leading-none group-hover:text-blue-600 transition-colors">{catTeams.length}</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Equipes</p>
                      </div>
                      <div>
                        <p className="text-[24px] font-black text-slate-900 leading-none group-hover:text-blue-600 transition-colors">{catAthletes.length}</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Inscritos</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : activeTab === 'teams' ? (
          <div className="space-y-6 animate-in fade-in duration-500">
            <TeamManager onAddTeam={handleAddTeam} />
            
            {currentUser.role === 'ADMIN' ? (
              <div className="space-y-8 mt-8">
                {Object.values(Category).map(cat => {
                   const catTeams = visibleTeams.filter(t => t.category === cat);
                   if (catTeams.length === 0) return null;
                   return (
                     <div key={cat} className="space-y-4">
                       <div className="flex items-center gap-4">
                         <h3 className="font-black text-slate-800 text-lg uppercase tracking-tight">{cat}</h3>
                         <div className="flex-1 h-px bg-slate-200"></div>
                         <span className="text-[10px] bg-white border border-slate-100 shadow-sm px-3 py-1 rounded-full font-black text-slate-500">{catTeams.length} EQUIPES</span>
                       </div>
                       
                       <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden">
                         <div className="overflow-x-auto">
                           <table className="w-full text-left border-collapse">
                             <thead>
                               <tr className="bg-slate-50/50 text-[10px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100">
                                 <th className="px-6 py-4">Equipe</th>
                                 <th className="px-6 py-4">Técnico</th>
                                 <th className="px-6 py-4">Responsável</th>
                                 <th className="px-6 py-4 text-center">Atletas</th>
                                 <th className="px-6 py-4 text-right">Ações</th>
                               </tr>
                             </thead>
                             <tbody className="divide-y divide-slate-50">
                               {catTeams.map(t => {
                                 const creator = systemUsers.find(u => u.id === t.created_by);
                                 const athleteCount = athletes.filter(a => a.teamId === t.id).length;
                                 return (
                                   <tr key={t.id} className="hover:bg-blue-50/10 transition-colors">
                                     <td className="px-6 py-4">
                                       <div className="font-black text-slate-900">{t.name}</div>
                                       <div className="text-[10px] text-slate-400 font-medium">{t.city} / {t.state}</div>
                                     </td>
                                     <td className="px-6 py-4 font-bold text-slate-700 text-sm">{t.coachName || 'N/A'}</td>
                                     <td className="px-6 py-4">
                                       <div className="text-[10px] font-black text-blue-600 uppercase tracking-tighter">{creator?.name || 'Sistema'}</div>
                                       <div className="text-[9px] text-slate-400 font-mono">@{creator?.username || 'admin'}</div>
                                     </td>
                                     <td className="px-6 py-4 text-center">
                                       <span className={`inline-block px-2 py-1 rounded-lg text-[10px] font-black ${athleteCount >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                         {athleteCount} Inscritos
                                       </span>
                                     </td>
                                     <td className="px-6 py-4 text-right">
                                       <div className="flex justify-end gap-1">
                                         <button onClick={() => { setEditingItem(t); setIsModalOpen(true); }} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                                         <button onClick={() => handleDeleteTeam(t.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                                       </div>
                                     </td>
                                   </tr>
                                 );
                               })}
                             </tbody>
                           </table>
                         </div>
                       </div>
                     </div>
                   );
                })}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {visibleTeams.map(t => {
                  return (
                    <div key={t.id} className="bg-white p-6 rounded-[2rem] shadow-xl border border-slate-50 relative overflow-hidden group hover:border-blue-100 transition-all">
                      <div className="absolute top-0 right-0 w-24 h-24 bg-blue-50/50 rounded-full -mr-12 -mt-12 transition-all group-hover:scale-110"></div>
                      <div className="flex justify-between items-start mb-4 relative z-10">
                        <div>
                          <h3 className="font-black text-xl text-slate-800 leading-tight">{t.name}</h3>
                          <div className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-black rounded-md uppercase mt-1">{t.category}</div>
                        </div>
                      </div>
                      <div className="space-y-3 relative z-10">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-slate-50 rounded-xl flex items-center justify-center text-blue-600">
                             <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          </div>
                          <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-0.5">Técnico Principal</p>
                            <p className="text-sm font-bold text-slate-700">{t.coachName || 'Não informado'}</p>
                          </div>
                        </div>
                        <div className="pt-3 border-t border-slate-50 flex items-center justify-between">
                          <div className="flex items-center gap-1.5 grayscale opacity-70">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                            <span className="text-[10px] font-black uppercase text-slate-400">{t.city} / {t.state}</span>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => { setEditingItem(t); setIsModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"><svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                            <button onClick={() => handleDeleteTeam(t.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all"><svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : activeTab === 'athletes' ? (
          <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden animate-in fade-in duration-500">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
              <div>
                <h2 className="font-black text-slate-800 uppercase text-lg tracking-tight">Atletas Registrados</h2>
                <p className="text-xs text-slate-400 font-medium">{visibleAthletes.length} atletas encontrados no total</p>
              </div>
              <div className="flex gap-3">
                <button onClick={openAddAthlete} className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-blue-100">Novo Atleta</button>
                <button onClick={handleExportToExcel} className="bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all">Excel</button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 text-[10px] font-black uppercase text-slate-400 tracking-widest border-b border-slate-100">
                    <th className="px-8 py-5">Atleta</th>
                    <th className="px-6 py-5">Contato</th>
                    <th className="px-6 py-5">Documentos</th>
                    <th className="px-6 py-5">Vínculo</th>
                    {currentUser.role === 'ADMIN' && <th className="px-6 py-5">Gestor</th>}
                    <th className="px-6 py-5 text-right">Inscrição</th>
                    <th className="px-8 py-5 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {visibleAthletes.length === 0 ? (
                    <tr><td colSpan={currentUser.role === 'ADMIN' ? 7 : 6} className="px-8 py-20 text-center text-slate-400 font-bold italic">Nenhum atleta encontrado</td></tr>
                  ) : (
                    visibleAthletes.map(a => {
                      const creator = systemUsers.find(u => u.id === a.created_by);
                      const athleteTeam = teams.find(t => t.id === a.teamId);
                      return (
                        <tr key={a.id} className="hover:bg-slate-50/80 transition-colors group">
                          <td className="px-8 py-4">
                            <div className="font-black text-slate-900 leading-tight">{a.fullName}</div>
                            <div className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">Nasc: {a.birthDate}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-xs font-bold text-slate-700">{a.phone}</div>
                            <div className="text-[10px] text-slate-400 font-medium">{a.city || 'N/A'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-[10px] font-mono text-slate-600 flex items-center gap-1.5"><span className="text-slate-300">CPF</span> {a.cpf}</div>
                            <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5"><span className="text-slate-200">RG </span> {a.rg || a.identity || '---'}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-black rounded-md uppercase mb-1">{a.category}</div>
                            {athleteTeam && (
                              <div className="text-[9px] font-black text-indigo-500 uppercase flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full"></span>
                                {athleteTeam.name}
                              </div>
                            )}
                          </td>
                          {currentUser.role === 'ADMIN' && (
                            <td className="px-6 py-4">
                              <div className="text-[10px] font-bold text-slate-800 uppercase tracking-tighter">{creator?.name || 'Sistema'}</div>
                            </td>
                          )}
                          <td className="px-6 py-4 text-right">
                            <div className="font-black text-slate-900 text-sm">{formatCurrency(a.totalValue)}</div>
                            <div className="text-[9px] text-emerald-600 font-black uppercase">Nível {a.registrationLevel}</div>
                          </td>
                          <td className="px-8 py-4 text-right">
                            <div className="flex justify-end gap-1">
                              <button onClick={() => openEditAthlete(a)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg></button>
                              <button onClick={() => handleDeleteAthlete(a.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-[2rem] shadow-sm border border-slate-100 overflow-hidden animate-in fade-in duration-500">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-slate-50/30">
              <div>
                <h2 className="font-black text-slate-800 uppercase text-lg tracking-tight">Gestores do Sistema</h2>
                <p className="text-xs text-slate-400 font-medium">Gerencie quem pode acessar esta central</p>
              </div>
              <button onClick={() => openManageUser()} className="bg-slate-900 hover:bg-black text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest transition-all">Novo Usuário</button>
            </div>
            <div className="divide-y divide-slate-100">
              {systemUsers.map(u => (
                <div key={u.id} className="p-6 flex justify-between items-center hover:bg-slate-50/50 transition-all">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-lg ${u.role === 'ADMIN' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                      {u.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-black text-slate-900 uppercase tracking-tight">{u.name}</div>
                      <div className="text-xs text-slate-400 font-mono">@{u.username} • <span className="text-blue-600 font-bold uppercase text-[9px] tracking-widest">{u.role}</span></div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => openManageUser(u)} className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all font-bold text-xs uppercase tracking-widest">Editar</button>
                    {u.id !== currentUser.id && u.id !== 'admin_master' && (
                      <button onClick={() => handleDeleteUser(u.id)} className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all font-bold text-xs uppercase tracking-widest">Excluir</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isModalOpen && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative animate-in fade-in zoom-in duration-300">
              <button onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center transition-all z-10 font-bold">✕</button>
              
              <div className="p-8">
                {activeTab === 'users' ? (
                   <form onSubmit={(e) => {
                     e.preventDefault();
                     const fd = new FormData(e.currentTarget);
                     handleManageUser(Object.fromEntries(fd));
                   }} className="space-y-6">
                     <h2 className="text-2xl font-black text-slate-800 mb-6">{editingItem ? 'Editar Usuário' : 'Novo Usuário (Gestor)'}</h2>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       <input name="name" defaultValue={editingItem?.name} required placeholder="Nome do Gestor" className="border border-slate-200 p-4 rounded-xl w-full focus:ring-2 focus:ring-blue-100 outline-none transition-all" />
                       <input name="username" defaultValue={editingItem?.username} required placeholder="E-mail / Usuário" className="border border-slate-200 p-4 rounded-xl w-full focus:ring-2 focus:ring-blue-100 outline-none transition-all" />
                       <input name="password" type="password" required={!editingItem} placeholder={editingItem ? "Senha (deixe em branco para manter)" : "Senha de acesso"} className="border border-slate-200 p-4 rounded-xl w-full focus:ring-2 focus:ring-blue-100 outline-none transition-all" />
                       <select name="role" defaultValue={editingItem?.role || 'MANAGER'} className="border border-slate-200 p-4 rounded-xl w-full focus:ring-2 focus:ring-blue-100 outline-none transition-all bg-white">
                         <option value="MANAGER">Gestor (Apenas suas equipes)</option>
                         <option value="ADMIN">Administrador (Tudo)</option>
                       </select>
                     </div>
                     <button type="submit" className="w-full bg-blue-600 text-white font-black py-5 rounded-2xl shadow-xl shadow-blue-100 mt-4 hover:bg-blue-700 transition-all uppercase tracking-widest text-xs">Salvar Alterações</button>
                   </form>
                ) : activeTab === 'teams' ? (
                  <>
                    <h2 className="text-2xl font-black text-slate-800 mb-6">{editingItem ? 'Editar Equipe' : 'Configurar Nova Equipe'}</h2>
                    <TeamManager 
                      onAddTeam={(data) => editingItem ? handleUpdateTeam(editingItem.id, data) : handleAddTeam(data)} 
                      initialData={editingItem} 
                    />
                  </>
                ) : (
                  <>
                    <h2 className="text-3xl font-black text-slate-800 mb-6">{editingItem ? 'Ficha do Atleta' : 'Ficha de Inscrição'}</h2>
                    <AthleteForm 
                      onAdd={(data) => editingItem ? handleUpdateAthlete(editingItem.id, data) : handleAddAthlete(data)} 
                      teams={teams} 
                      initialData={editingItem}
                      compact 
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>

      {toast && (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-8 py-4 rounded-2xl shadow-2xl font-black text-xs uppercase tracking-widest animate-in fade-in slide-in-from-bottom-4 duration-300 z-[100] ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-slate-900 text-white'}`}>
          <div className="flex items-center gap-3">
            {toast.type === 'success' ? '✓' : 'ℹ'}
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;